import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import Stepper from "@/components/Stepper"
import { ConfettiModal, WarningModal } from "@/components/SubmitResultModals"
import { useSelfQuery } from "@/data/queries"
import {
  useCreateBloodGlucoseFormMutation,
  useObservationByIdQuery,
} from "@/data/queries/measurementQueries"
import { useMealMeasurement, useResetMealMeasurement } from "@/data/store"
import { toMedicationLog, type MedicationTaken } from "@/model/BloodGlucoseForm"
import type { BloodGlucoseFormContext } from "@/model/BloodGlucoseFormContext"
import { textForFormContext } from "@/model/BloodGlucoseFormContext"
import { hasPrescribedMedicationForContext } from "@/model/Medication"
import { ROUTES } from "@/routes"
import { useEffect, useState } from "react"
import { Navigate, useLocation, useNavigate } from "react-router"
import { toast } from "sonner"
import MealContextPickerStep from "./steps/MealContextPickerStep"
import MealDetailsStep from "./steps/MealDetailsStep"
import MedicationQuestionStep from "./steps/MedicationQuestionStep"
import NoteScreenStep from "./steps/NoteScreenStep"

const MEAL_CONTENT_TITLE = "What did you eat and drink during your meal?"

const CRITICAL_CLASSIFICATIONS = new Set(["emergency-low", "critical-low", "critical-high"])

type WarningState = { reading: number | null; message: string | null }

const MealMeasurementPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as { readingId?: string; initialTab?: string } | null
  const readingId = state?.readingId ?? null
  const dashboardReturnUrl = `${ROUTES.DASHBOARD}?tab=${state?.initialTab ?? "overview"}`
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""
  const { mutateAsync: createForm, isPending } = useCreateBloodGlucoseFormMutation({ patientId })
  const { data: observation } = useObservationByIdQuery({ readingId })

  const [meal, setMeal] = useMealMeasurement()
  const resetMeal = useResetMealMeasurement()
  const [currentStep, setCurrentStep] = useState(0)
  const [warning, setWarning] = useState<WarningState | null>(null)
  const [confettiOpen, setConfettiOpen] = useState(false)

  useEffect(() => {
    resetMeal()
  }, [resetMeal])

  if (!readingId) {
    return <Navigate to={`${ROUTES.DASHBOARD}?tab=overview`} replace />
  }

  const goNext = () => setCurrentStep((step) => step + 1)

  const onChangeContext = (value: BloodGlucoseFormContext) => {
    setMeal((current) => ({ ...current, context: value }))
    goNext()
  }

  const onMedication = (values: { medicationTaken: MedicationTaken | null; note: string }) => {
    setMeal((current) => ({
      ...current,
      medicationTaken: values.medicationTaken,
      medicationNote: values.note,
    }))
    goNext()
  }

  const onMealDetails = (values: {
    mealContents: string
    carbsInGrams: string | number
    proteinInGrams: string | number
  }) => {
    setMeal((current) => ({ ...current, ...values }))
    goNext()
  }

  const onNotes = async ({ notes }: { notes: string }) => {
    if (!meal.context) return
    setMeal((current) => ({ ...current, notes }))
    try {
      await createForm({
        patientId,
        form: {
          context: meal.context,
          dateTime: null,
          notes: notes || null,
          mealContents: meal.mealContents || null,
          carbsInGrams: meal.carbsInGrams === "" ? null : Number(meal.carbsInGrams),
          proteinInGrams: meal.proteinInGrams === "" ? null : Number(meal.proteinInGrams),
          medications: toMedicationLog(meal.medicationTaken, meal.medicationNote),
          reasons: null,
          readingId,
        },
      })
      // The form-create response doesn't include the reading; pull from the
      // observation we already fetched for the modal.
      const classification = observation?.reading?.classification
      const reading = observation?.reading?.bloodGlucoseInMilligramsPerDeciliter
      if (classification && CRITICAL_CLASSIFICATIONS.has(classification)) {
        setWarning({
          reading: reading ?? null,
          message: observation?.note ?? null,
        })
      } else {
        setConfettiOpen(true)
      }
    } catch (error) {
      const isLocked = error instanceof Error && /423/.test(error.message)
      toast.error(
        isLocked
          ? "Your doctor has already reviewed your measurement. It is no longer possible to submit the form"
          : "There was a problem submitting the form. Please try again later, or contact Bloomlife Support",
      )
    }
  }

  const dismissAndGoToDashboard = () => {
    resetMeal()
    setWarning(null)
    setConfettiOpen(false)
    navigate(dashboardReturnUrl)
  }

  if (!self) {
    return (
      <Page showAppbar={false}>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Page>
    )
  }

  const contextLabel = meal.context ? textForFormContext(meal.context) : ""

  return (
    <Page showAppbar={false}>
      <Stepper
        currentStep={currentStep}
        steps={[
          <MealContextPickerStep
            key="context"
            context={meal.context}
            onChangeMealContext={onChangeContext}
            onBack={() => navigate(dashboardReturnUrl)}
          />,
          ...(meal.context &&
          hasPrescribedMedicationForContext(self.prescribedDiabetesMedications, meal.context)
            ? [
                <MedicationQuestionStep
                  key="medication"
                  medicationTaken={meal.medicationTaken}
                  note={meal.medicationNote}
                  onSubmit={onMedication}
                  onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
                />,
              ]
            : []),
          <MealDetailsStep
            key="details"
            context={contextLabel}
            mealContentTitle={MEAL_CONTENT_TITLE}
            initialValues={{
              mealContents: meal.mealContents,
              carbsInGrams: meal.carbsInGrams,
              proteinInGrams: meal.proteinInGrams,
            }}
            onSubmit={onMealDetails}
            onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
          />,
          <NoteScreenStep
            key="notes"
            context={contextLabel}
            initialValues={{ notes: meal.notes }}
            isLoading={isPending}
            onSubmit={onNotes}
            onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
          />,
        ]}
      />

      <WarningModal
        open={warning !== null}
        reading={warning?.reading ?? null}
        message={warning?.message ?? null}
        onClose={dismissAndGoToDashboard}
      />
      <ConfettiModal open={confettiOpen} onDismiss={dismissAndGoToDashboard} />
    </Page>
  )
}

export default MealMeasurementPage
